import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api, { reportsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { toast } from 'sonner';
import {
  DownloadSimple, ArrowsClockwise, X, CaretLeft, CaretRight,
  Receipt, Stethoscope,
} from '@phosphor-icons/react';

const money = (v) => (v === '' || v == null) ? '' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
const num = (v) => (v === '' || v == null) ? '' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// 32 columns matching the client's item-wise template (INR ⇒ BCY == FCY)
const COLS = [
  { h: 'Customer Name', k: 'customer_name', kind: 'text', w: 'min-w-[180px]' },
  { h: 'Customer GST', k: 'customer_gstin', kind: 'text', w: 'min-w-[140px]' },
  { h: 'Place of Supply', k: 'place_of_supply', kind: 'text' },
  { h: 'Date', k: 'date', kind: 'date' },
  { h: 'Transaction#', k: 'transaction_no', kind: 'mono' },
  { h: 'Reference#', k: 'reference_no', kind: 'text' },
  { h: 'Status', k: 'status', kind: 'text' },
  { h: 'Item Name', k: 'item_name', kind: 'text', w: 'min-w-[200px]' },
  { h: 'Quantity Ordered', k: 'quantity', kind: 'num', align: 'right' },
  { h: 'Item Price (BCY)', k: 'item_price', kind: 'money', align: 'right' },
  { h: 'Total (BCY)', k: 'total_ex_tax', kind: 'money', align: 'right' },
  { h: 'Sales person', k: 'salesperson', kind: 'text' },
  { h: 'Second Audiologist', k: 'second_audiologist', kind: 'text' },
  { h: 'Tax Amount (BCY)', k: 'tax_amount', kind: 'money', align: 'right' },
  { h: 'CGST', k: 'cgst', kind: 'money', align: 'right' },
  { h: 'SGST', k: 'sgst', kind: 'money', align: 'right' },
  { h: 'IGST', k: 'igst', kind: 'money', align: 'right' },
  { h: 'Total (FCY)', k: 'total_ex_tax', kind: 'money', align: 'right' },
  { h: 'Total with Tax(FCY)', k: 'total_inc_tax', kind: 'money', align: 'right' },
  { h: 'Total with Tax', k: 'total_inc_tax', kind: 'money', align: 'right' },
  { h: 'Item Price (FCY)', k: 'item_price', kind: 'money', align: 'right' },
  { h: 'HSN/SAC', k: 'hsn_sac', kind: 'text' },
  { h: 'Item Description', k: 'item_description', kind: 'text', w: 'min-w-[160px]' },
  { h: 'Tax Percentage', k: 'tax_percentage', kind: 'num', align: 'right' },
  { h: 'Tax Amount (FCY)', k: 'tax_amount', kind: 'money', align: 'right' },
  { h: 'Total', k: 'invoice_total', kind: 'money', align: 'right' },
  { h: 'Balance', k: 'balance', kind: 'money', align: 'right' },
  { h: 'Notes', k: 'notes', kind: 'text', w: 'min-w-[160px]' },
  { h: 'Lead Source', k: 'lead_source', kind: 'text' },
  { h: 'Credit Note Amount', k: 'credit_note_amount', kind: 'money', align: 'right' },
  { h: 'Invoice Type', k: 'invoice_type', kind: 'text' },
  { h: 'Lead Source (Credit Note)', k: 'lead_source_cn', kind: 'text' },
  { h: 'Mobile Phone', k: 'mobile_phone', kind: 'text' },
  { h: 'Work Phone', k: 'work_phone', kind: 'text' },
  { h: 'Phone No', k: 'phone_no', kind: 'text' },
  { h: 'Warehouse Location Name', k: 'warehouse_location', kind: 'text', w: 'min-w-[160px]' },
  { h: 'Serial Number', k: 'serial_number', kind: 'text', w: 'min-w-[140px]' },
];

const cell = (r, c) => {
  let v = r[c.k];
  if (v && typeof v === 'object') v = v.name ?? JSON.stringify(v);
  if (c.kind === 'money') return money(v);
  if (c.kind === 'date') return fmtD(v);
  if (c.kind === 'num') return num(v);
  return v ?? '';
};

const PAGE_SIZE = 50;

export default function ItemWiseReportPage() {
  const now = new Date();
  const fyStart = now.getMonth() >= 3 ? `${now.getFullYear()}-04-01` : `${now.getFullYear() - 1}-04-01`;
  const fyEnd = now.getMonth() >= 3 ? `${now.getFullYear() + 1}-03-31` : `${now.getFullYear()}-03-31`;

  const [from, setFrom] = useState(fyStart);
  const [to, setTo] = useState(fyEnd);
  const [clinic, setClinic] = useState('');
  const [audiologist, setAudiologist] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [status, setStatus] = useState('');
  const [invoiceType, setInvoiceType] = useState('');
  const [customer, setCustomer] = useState('');
  const [item, setItem] = useState('');
  const [serial, setSerial] = useState('');
  const [includeST, setIncludeST] = useState(false);

  const [options, setOptions] = useState({ audiologists: [], clinics: [], statuses: [], lead_sources: [], invoice_types: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState('lines');
  const [incPct, setIncPct] = useState('2');
  const [useSlabs, setUseSlabs] = useState(false);
  const [slabs, setSlabs] = useState([
    { min: 0, max: 500000, pct: 1 },
    { min: 500000, max: 1000000, pct: 2 },
    { min: 1000000, max: '', pct: 3 },
  ]);

  useEffect(() => {
    reportsAPI.itemWiseFilterOptions().then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const buildParams = useCallback(() => {
    const p = { start_date: from, end_date: to, include_stock_transfer: includeST };
    if (clinic) p.clinic = clinic;
    if (audiologist) p.audiologist = audiologist;
    if (leadSource) p.lead_source = leadSource;
    if (status) p.status = status;
    if (invoiceType) p.invoice_type = invoiceType;
    if (customer) p.customer = customer;
    if (item) p.item = item;
    if (serial) p.serial = serial;
    return p;
  }, [from, to, includeST, clinic, audiologist, leadSource, status, invoiceType, customer, item, serial]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await reportsAPI.itemWise({ ...buildParams(), page, page_size: PAGE_SIZE });
      setData(data);
    } catch { toast.error('Failed to load item-wise report'); }
    finally { setLoading(false); }
  }, [buildParams, page]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => { setPage(1); load(); };
  const clearFilters = () => {
    setClinic(''); setAudiologist(''); setLeadSource(''); setStatus(''); setInvoiceType('');
    setCustomer(''); setItem(''); setSerial(''); setPage(1);
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const resp = await api.get('/reports/item-wise/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `item_wise_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const summary = data?.summary || {};
  const totalPages = Math.ceil((data?.total || 0) / PAGE_SIZE);

  const rateFor = (net) => {
    if (!useSlabs) return Number(incPct) || 0;
    const s = slabs.find(sl => net >= Number(sl.min || 0) && (sl.max === '' || sl.max == null || net < Number(sl.max)));
    return s ? (Number(s.pct) || 0) : 0;
  };
  const incRows = (data?.by_audiologist || []).map(a => {
    const rate = rateFor(a.net_sales);
    return { ...a, rate, payout: Math.round(a.net_sales * rate) / 100 };
  });
  const totalPayout = incRows.reduce((s, r) => s + r.payout, 0);
  const totalNet = incRows.reduce((s, r) => s + r.net_sales, 0);
  const updateSlab = (i, field, val) => setSlabs(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  const addSlab = () => setSlabs(prev => [...prev, { min: 0, max: '', pct: 0 }]);
  const removeSlab = (i) => setSlabs(prev => prev.filter((_, idx) => idx !== i));
  const exportIncentiveCsv = () => {
    const header = ['Audiologist', 'Line Items', 'Qty', 'Net Sales (ex-Tax)', 'Incentive %', 'Incentive Payout'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(',')].concat(incRows.map(r => [r.audiologist, r.line_count, r.qty, r.net_sales, r.rate, r.payout].map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audiologist_incentive_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6" data-testid="item-wise-report-page">
      <PageHeader title="Item-wise Report" subtitle="Incentive Report — Audiologists · one row per invoice line with serials, tax & credit-note attribution" />

      {/* Filters */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">FROM</label>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 text-xs w-36" data-testid="iw-from" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">TO</label>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 text-xs w-36" data-testid="iw-to" />
          </div>
          <Button size="sm" variant="outline" onClick={() => { setFrom(fyStart); setTo(fyEnd); }} className="h-8 text-xs">Current FY</Button>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-[#0F172A] ml-2" data-testid="iw-st-toggle">
            <input type="checkbox" checked={includeST} onChange={e => setIncludeST(e.target.checked)} className="accent-[#0F4C81]" />
            Include Stock Transfers
          </label>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CLINIC</label>
            <MultiSelectFilter value={String(clinic || '').split(',').filter(Boolean)} onChange={vals => setClinic(vals.join(','))}
              options={(options.clinics || []).map(o => ({ value: o, label: o }))} allLabel="All Clinics" noun="selected" testid="iw-clinic" buttonClassName="h-8 text-xs w-44" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">AUDIOLOGIST</label>
            <MultiSelectFilter value={String(audiologist || '').split(',').filter(Boolean)} onChange={vals => setAudiologist(vals.join(','))}
              options={(options.audiologists || []).map(o => ({ value: o, label: o }))} allLabel="All Audiologists" noun="selected" testid="iw-audiologist" buttonClassName="h-8 text-xs w-44" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">LEAD SOURCE</label>
            <MultiSelectFilter value={String(leadSource || '').split(',').filter(Boolean)} onChange={vals => setLeadSource(vals.join(','))}
              options={(options.lead_sources || []).map(o => ({ value: o, label: o }))} allLabel="All Sources" noun="selected" testid="iw-lead-source" buttonClassName="h-8 text-xs w-40" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">STATUS</label>
            <MultiSelectFilter value={String(status || '').split(',').filter(Boolean)} onChange={vals => setStatus(vals.join(','))}
              options={(options.statuses || []).map(o => ({ value: o, label: o }))} allLabel="All Status" noun="selected" testid="iw-status" buttonClassName="h-8 text-xs w-36" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">INVOICE TYPE</label>
            <MultiSelectFilter value={String(invoiceType || '').split(',').filter(Boolean)} onChange={vals => setInvoiceType(vals.join(','))}
              options={(options.invoice_types || []).map(o => ({ value: o, label: o }))} allLabel="All Types" noun="selected" testid="iw-invoice-type" buttonClassName="h-8 text-xs w-40" />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">CUSTOMER</label>
            <Input value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Name / phone" className="h-8 text-xs w-40" data-testid="iw-customer" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">ITEM</label>
            <Input value={item} onChange={e => setItem(e.target.value)} placeholder="Search item" className="h-8 text-xs w-40" data-testid="iw-item" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">SERIAL NUMBER</label>
            <Input value={serial} onChange={e => setSerial(e.target.value)} placeholder="Search serial" className="h-8 text-xs w-40" data-testid="iw-serial" />
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" onClick={clearFilters} className="h-8 text-xs"><X size={12} className="mr-1" /> Clear</Button>
            <Button size="sm" onClick={applyFilters} className="h-8 text-xs bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="iw-apply"><ArrowsClockwise size={12} className="mr-1" /> Apply</Button>
            <Button size="sm" variant="outline" onClick={exportExcel} disabled={exporting} className="h-8 text-xs" data-testid="iw-export"><DownloadSimple size={12} className="mr-1" /> {exporting ? 'Exporting…' : 'Export Excel'}</Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
        {[
          { label: 'Total (ex-Tax)', value: money(summary.total_ex_tax), color: 'bg-[#EBF3FA] text-[#0F4C81]', testId: 'iw-sum-extax' },
          { label: 'Total Tax', value: money(summary.total_tax), color: 'bg-amber-50 text-amber-700', testId: 'iw-sum-tax' },
          { label: 'Total (inc-Tax)', value: money(summary.total_inc_tax), color: 'bg-[#0F4C81] text-white', testId: 'iw-sum-inctax' },
          { label: 'Total Qty', value: num(summary.total_qty), color: 'bg-blue-50 text-blue-800', testId: 'iw-sum-qty' },
          { label: 'Line Items', value: (summary.line_count || 0).toLocaleString('en-IN'), color: 'bg-purple-50 text-purple-800', testId: 'iw-sum-count' },
        ].map((k, i) => (
          <div key={i} className={`rounded-xl p-4 ${k.color}`} data-testid={k.testId}>
            <p className="text-[10px] uppercase tracking-wide opacity-70">{k.label}</p>
            <p className="text-xl font-bold mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E2E8F0] mt-6 mb-4">
        {[{ k: 'lines', label: 'Line Items', icon: Receipt }, { k: 'incentive', label: 'By Audiologist (Incentive)', icon: Stethoscope }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${tab === t.k ? 'border-[#0F4C81] text-[#0F4C81]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            data-testid={`iw-tab-${t.k}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'lines' && (
      <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full text-xs whitespace-nowrap" data-testid="iw-table">
            <thead>
              <tr className="bg-[#F1F5F9] text-[#475569] text-[10px] uppercase border-b border-[#E2E8F0]">
                {COLS.map(c => (
                  <th key={c.h} className={`p-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.w || ''}`}>{c.h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <tr key={i} className={`border-t border-[#E2E8F0] hover:bg-[#F8FAFC] ${r.source === 'credit_note' ? 'bg-red-50/60 text-red-700' : ''}`} data-testid={`iw-row-${i}`}>
                  {COLS.map(c => (
                    <td key={c.h} className={`p-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.kind === 'mono' ? 'font-mono text-[#0F4C81]' : ''} ${c.w ? 'max-w-[240px] truncate' : ''}`} title={c.w ? String(r[c.k] ?? '') : undefined}>
                      {cell(r, c)}
                    </td>
                  ))}
                </tr>
              ))}
              {!loading && !data?.rows?.length && (
                <tr><td colSpan={COLS.length} className="text-center py-8 text-[#64748B]">No matching line items for the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        )}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] sticky left-0">
            <span className="text-xs text-[#64748B]">Page {page} of {totalPages} · {(data?.total || 0).toLocaleString('en-IN')} line items</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><CaretLeft size={14} /></Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><CaretRight size={14} /></Button>
            </div>
          </div>
        )}
      </div>
      )}

      {tab === 'incentive' && (
        <div className="space-y-4" data-testid="iw-incentive-panel">
          {/* Config */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-xs text-[#0F172A] cursor-pointer">
                <input type="radio" name="inc-mode" checked={!useSlabs} onChange={() => setUseSlabs(false)} className="accent-[#0F4C81]" data-testid="inc-mode-flat" />
                Flat %
              </label>
              <div className={`flex items-center gap-2 ${useSlabs ? 'opacity-40' : ''}`}>
                <Input type="number" value={incPct} onChange={e => setIncPct(e.target.value)} disabled={useSlabs} className="h-8 text-xs w-24" data-testid="inc-flat-pct" />
                <span className="text-xs text-[#64748B]">% of net sales</span>
              </div>
              <label className="flex items-center gap-2 text-xs text-[#0F172A] cursor-pointer">
                <input type="radio" name="inc-mode" checked={useSlabs} onChange={() => setUseSlabs(true)} className="accent-[#0F4C81]" data-testid="inc-mode-slab" />
                Slab-based
              </label>
              <Button size="sm" variant="outline" onClick={exportIncentiveCsv} className="h-8 text-xs ml-auto" data-testid="inc-export"><DownloadSimple size={12} className="mr-1" /> Export CSV</Button>
            </div>
            {useSlabs && (
              <div className="mt-4 border-t border-[#E2E8F0] pt-3">
                <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-2">Incentive Slabs (net sales range → %)</p>
                <div className="space-y-2">
                  {slabs.map((sl, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs" data-testid={`inc-slab-${i}`}>
                      <span className="text-[#64748B]">₹</span>
                      <Input type="number" value={sl.min} onChange={e => updateSlab(i, 'min', e.target.value)} placeholder="Min" className="h-8 w-28" />
                      <span className="text-[#64748B]">to ₹</span>
                      <Input type="number" value={sl.max} onChange={e => updateSlab(i, 'max', e.target.value)} placeholder="∞" className="h-8 w-28" />
                      <span className="text-[#64748B]">→</span>
                      <Input type="number" value={sl.pct} onChange={e => updateSlab(i, 'pct', e.target.value)} placeholder="%" className="h-8 w-20" />
                      <span className="text-[#64748B]">%</span>
                      <Button size="sm" variant="ghost" onClick={() => removeSlab(i)} className="h-7 px-2 text-red-400 hover:text-red-600"><X size={12} /></Button>
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={addSlab} className="h-7 text-xs mt-2">+ Add slab</Button>
              </div>
            )}
          </div>

          {/* Payout summary */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-xl p-4 bg-[#EBF3FA] text-[#0F4C81]" data-testid="inc-sum-netsales"><p className="text-[10px] uppercase tracking-wide opacity-70">Total Net Sales</p><p className="text-xl font-bold mt-1">{money(totalNet)}</p></div>
            <div className="rounded-xl p-4 bg-[#0F4C81] text-white" data-testid="inc-sum-payout"><p className="text-[10px] uppercase tracking-wide opacity-70">Total Incentive Payout</p><p className="text-xl font-bold mt-1">{money(totalPayout)}</p></div>
            <div className="rounded-xl p-4 bg-purple-50 text-purple-800" data-testid="inc-sum-auds"><p className="text-[10px] uppercase tracking-wide opacity-70">Audiologists</p><p className="text-xl font-bold mt-1">{incRows.length}</p></div>
          </div>

          {/* Payout table */}
          <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto">
            <table className="w-full text-xs" data-testid="inc-table">
              <thead>
                <tr className="bg-[#F1F5F9] text-[#475569] text-[10px] uppercase border-b border-[#E2E8F0]">
                  <th className="text-left p-2.5 min-w-[200px]">Audiologist</th>
                  <th className="text-right p-2.5">Line Items</th>
                  <th className="text-right p-2.5">Qty</th>
                  <th className="text-right p-2.5">Net Sales (ex-Tax)</th>
                  <th className="text-right p-2.5">Incentive %</th>
                  <th className="text-right p-2.5">Incentive Payout</th>
                </tr>
              </thead>
              <tbody>
                {incRows.map((r, i) => (
                  <tr key={i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`inc-row-${i}`}>
                    <td className="p-2.5 font-medium">{r.audiologist}</td>
                    <td className="p-2.5 text-right">{r.line_count}</td>
                    <td className="p-2.5 text-right">{r.qty}</td>
                    <td className="p-2.5 text-right font-medium">{money(r.net_sales)}</td>
                    <td className="p-2.5 text-right">{r.rate}%</td>
                    <td className="p-2.5 text-right font-semibold text-emerald-700">{money(r.payout)}</td>
                  </tr>
                ))}
                {!incRows.length && <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No audiologist sales for the selected filters.</td></tr>}
              </tbody>
              {incRows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-[#0F4C81] bg-[#F8FAFC] font-semibold">
                    <td className="p-2.5">Total</td>
                    <td className="p-2.5 text-right">{incRows.reduce((s, r) => s + r.line_count, 0)}</td>
                    <td className="p-2.5 text-right">{incRows.reduce((s, r) => s + r.qty, 0)}</td>
                    <td className="p-2.5 text-right">{money(totalNet)}</td>
                    <td className="p-2.5"></td>
                    <td className="p-2.5 text-right text-emerald-700">{money(totalPayout)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <p className="text-[10px] text-[#64748B]">Incentive is computed on net sales (line total excluding tax, after discounts), credited to the primary sales person. Adjust % / slabs above — recalculates instantly. Totals reflect all matching lines for the selected filters (not just the current page).</p>
        </div>
      )}
    </div>
  );
}
